/**
 * Shared auth postMessage handling for Office add-ins.
 *
 * This module provides utilities for handling device code OAuth authentication
 * via postMessage. It's used before the RPC handshake is established (auth
 * happens before the chat iframe loads).
 *
 * Each add-in implements callbacks to handle auth events according to its
 * specific requirements (UI updates, token storage, etc.).
 */

import type { OAuthSignInResponse, OAuthTokenResponse, TokenData } from "./types";

/**
 * Callbacks for handling auth postMessage events.
 * Each add-in implements these to handle auth flow according to its needs.
 */
export interface AuthCallbacks {
  /** Called when the auth page signals it has loaded */
  onSignInPageLoaded: () => void;
  /** Called when a device code URL is received */
  onDeviceCodeReceived: (url: string, expiresInSeconds: number) => void;
  /** Called when a valid token is received */
  onTokenReceived: (
    tokenData: TokenData,
    user: OAuthTokenResponse["user"],
    defaultWorkspaceId?: string,
  ) => void;
  /** Called when token response indicates an error (empty or invalid tokenData) */
  onTokenError: () => void;
}

/**
 * Type guard to check if data is an OAuthSignInResponse.
 */
function isOAuthSignInResponse(data: unknown): data is OAuthSignInResponse {
  return (
    typeof data === "object" &&
    data !== null &&
    "url" in data &&
    "expiresInSeconds" in data &&
    typeof (data as OAuthSignInResponse).url === "string" &&
    typeof (data as OAuthSignInResponse).expiresInSeconds === "number"
  );
}

/**
 * Type guard to check if data is an OAuthTokenResponse.
 */
function isOAuthTokenResponse(data: unknown): data is OAuthTokenResponse {
  return typeof data === "object" && data !== null && "tokenData" in data;
}

/**
 * Creates a message event handler for auth postMessage events.
 *
 * Handles origin validation and message parsing, then delegates to the
 * appropriate callback. Supports:
 * - "SIGN_IN_PAGE_LOADED" string event
 * - OAuthSignInResponse (device code URL)
 * - OAuthTokenResponse (token after auth)
 *
 * @param walterHost - The expected origin for messages (e.g., "https://app.getwalter.com")
 * @param callbacks - Handlers for each auth event type
 * @returns A message event handler function to pass to addEventListener
 *
 * @example
 * ```typescript
 * const handler = createAuthMessageHandler(WALTER_HOST, {
 *   onSignInPageLoaded: () => clearTimeout(loadingTimeout),
 *   onDeviceCodeReceived: (url, expiresIn) => setDeviceCodeUrl(url),
 *   onTokenReceived: (tokenData) => tokenStorage.save(tokenData),
 *   onTokenError: () => showError("Authentication failed"),
 * });
 *
 * window.addEventListener("message", handler);
 * ```
 */
export function createAuthMessageHandler(
  walterHost: string,
  callbacks: AuthCallbacks,
): (event: MessageEvent) => void {
  return (event: MessageEvent) => {
    // Validate origin
    if (event.origin !== walterHost) {
      return;
    }

    const data = event.data;

    // Handle string events
    if (data === "SIGN_IN_PAGE_LOADED") {
      callbacks.onSignInPageLoaded();
      return;
    }

    // Handle OAuth responses (object-based)
    if (typeof data === "object" && data !== null) {
      // OAuthSignInResponse (device code URL)
      if (isOAuthSignInResponse(data)) {
        callbacks.onDeviceCodeReceived(data.url, data.expiresInSeconds);
        return;
      }

      // OAuthTokenResponse
      if (isOAuthTokenResponse(data)) {
        // Empty string or missing accessToken indicates an error
        if (data.tokenData === "" || !data.tokenData?.accessToken) {
          callbacks.onTokenError();
          return;
        }
        callbacks.onTokenReceived(data.tokenData, data.user, data.defaultWorkspaceId);
      }
    }
  };
}

/**
 * Sends a request to the auth iframe to generate a new device code URL.
 *
 * @param iframe - The auth iframe element
 * @param walterHost - The Walter application host URL
 * @param scope - OAuth scopes to request (default: "read_data web_embed")
 */
export function requestDeviceCodeUrl(
  iframe: HTMLIFrameElement,
  walterHost: string,
  scope: string = "read_data web_embed",
): void {
  iframe.contentWindow?.postMessage({ requestType: "generateDeviceCodeUrl", scope }, walterHost);
}

/**
 * Gets the parent domain for cross-origin reference.
 * Useful for logging and debugging cross-origin issues.
 */
export function getParentDomain(): string {
  return window.location !== window.parent.location ? document.referrer : document.location.href;
}
